import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ContentLoader from "react-content-loader";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import axios from "axios";
import { getTrans as t } from "../../Localization/translations";
import Helper from "../../Helper";
import CreditMemoService from "../../services/CreditMemo/creditMemoService";
import authHeader from "../../services/auth-header";
import { API } from "../../backend";
import ApprovalAction from "../Approvals/ApprovalAction";
import ApprovalDetails from "../Approvals/ApprovalDetails";



export default function ViewCreditMemo() {
    const location = useLocation();
    const navigate = useNavigate();
    const creditMemoId = location.state;

    const [cm, setCm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dueInvoices, setDueInvoices] = useState([]);
    const [applyAmounts, setApplyAmounts] = useState({});
    const [applyLoading, setApplyLoading] = useState(null);
    const [reverseLoading, setReverseLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [appliedInvoices, setAppliedInvoices] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [reverseAppLoading, setReverseAppLoading] = useState(false);

    // Approval
    const [isApprovalRequired, setIsApprovalRequired] = useState(false);
    const [approvalTaskId, setApprovalTaskId] = useState(null);
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [approvalStatus, setApprovalStatus] = useState("Draft");
    const [approvalRequestId, setApprovalRequestId] = useState(null);
    const recordsPerPage = 1;

    // ── Load CM ───────────────────────────────────────────────────
    const loadCM = () => {
        CreditMemoService.getById(creditMemoId).then((res) => {
            const data = res.data;
            setCm(data);
            setAttachments(data.attachments || []);
            setLoading(false);

            if (data.customer_name) {
                axios.get(
                    API + `billing/invoiceswithdue/?per_page=100&page=1&search=&contractNumber=&customerName=${data.customer_name}&contractid=undefined`,
                    { headers: authHeader() }
                ).then((r) => {
                    setDueInvoices(r.data.data || r.data.results || []);
                }).catch(() => setDueInvoices([]));
            }
        }).catch(() => {
            toast.error("Failed to load credit memo");
            setLoading(false);
        });

        // Load actual application records
        axios.get(API + `creditmemo/${creditMemoId}/applications/`, { headers: authHeader() })
            .then((r) => setAppliedInvoices(r.data || []))
            .catch(() => setAppliedInvoices([]));
    };

    useEffect(() => {
        if (!creditMemoId) { navigate("/creditmemo"); return; }
        loadCM();
    }, [creditMemoId]);

    // ── Approval ──────────────────────────────────────────
    const normalizeStatus = (status) => {
        if (!status) return "Draft";

        const s = status.toLowerCase();

        if (s === "pending") return "Pending";
        if (s === "approved") return "approved";
        if (s === "rejected") return "rejected";

        return "Draft";
    };

    const approvalRequest = creditMemoId
        ? {
            id:
                normalizeStatus(approvalStatus) === "Draft"
                    ? creditMemoId
                    : approvalTaskId,
            status: normalizeStatus(approvalStatus),
        }
        : null;

    useEffect(() => {

        // Approval History
        axios
            .get(API + `approvals/history/${creditMemoId}/`, {
                headers: authHeader(),
                params: {
                    type: "creditmemo",
                },
            })
            .then((res) => {
                setApprovalHistory(res.data || []);
            })
            .catch(() => {
                setApprovalHistory([]);
            });

        // Approval Task
        axios
            .get(API + `approvals/task/${creditMemoId}/`, {
                headers: authHeader(),
                params: {
                    type: "creditmemo",
                },
            })
            .then((res) => {
                const data = res.data || {};

                setApprovalTaskId(data.task_id || null);
                setApprovalRequestId(data.request_id || null);

                const status = data.status?.toLowerCase() || "draft";

                const formattedStatus =
                    status === "pending"
                        ? "Pending"
                        : status === "approved"
                            ? "approved"
                            : status === "rejected"
                                ? "rejected"
                                : "Draft";

                setApprovalStatus(formattedStatus);
            })
            .catch(() => {
                setApprovalStatus("Draft");
                setApprovalTaskId(null);
            });

    }, [creditMemoId]);

    useEffect(() => {
        axios.get(API + "accounting/transactiontypesview/?transaction_type=Credit Memo", { headers: authHeader() })
            .then((res) => {
                const list = res.data.data || [];
                if (list.length > 0) {
                    const approvalFlag = (list[0].approval || "").trim().toUpperCase();
                    setIsApprovalRequired(approvalFlag === "Y");
                }
            })
            .catch(() => { });
    }, []);


    // ── Apply to invoice ──────────────────────────────────────────
    const handleApplyToInvoice = async (invoice) => {
        const applyAmt = parseFloat(applyAmounts[invoice.id] || 0);
        const unapplied = parseFloat(((cm.amount || 0) - (cm.applied_amount || 0)).toFixed(2));

        if (!applyAmt || applyAmt <= 0) { toast.error("Enter a valid apply amount"); return; }
        if (applyAmt > (invoice.balance_amount || 0)) { toast.error("Apply amount cannot exceed invoice remaining amount"); return; }
        if (applyAmt > unapplied) { toast.error(`Apply amount cannot exceed unapplied amount (${Helper.Amount(unapplied)})`); return; }

        setApplyLoading(invoice.id);
        try {
            await CreditMemoService.applyToInvoice(creditMemoId, invoice.id, applyAmt);
            toast.success("Credit memo applied to invoice successfully", { autoClose: 1000 });
            setApplyAmounts((prev) => ({ ...prev, [invoice.id]: "" }));
            loadCM();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Apply failed");
        } finally {
            setApplyLoading(null);
        }
    };

    // ── Reverse confirm ───────────────────────────────────────────
    const handleReverseConfirm = () => {
        let reverseComment = "";
        confirmAlert({
            customUI: ({ onClose }) => (
                <div className="react-confirm-alert-body">
                    <h6>Confirm reversal and provide reason.</h6>
                    <textarea
                        className="form-control mt-3"
                        placeholder="Enter reverse reason..."
                        onChange={(e) => (reverseComment = e.target.value)}
                    />
                    <div className="react-confirm-alert-button-group mt-3">
                        <button onClick={onClose}>Cancel</button>
                        <button onClick={() => { handleReverse(reverseComment); onClose(); }}>Save</button>
                    </div>
                </div>
            ),
        });
    };

    const handleReverse = async (reverseReason) => {
        setReverseLoading(true);
        try {
            await CreditMemoService.reverse(creditMemoId, reverseReason);
            toast.success("Credit memo reversed successfully", { autoClose: 1000 });
            setTimeout(() => loadCM(), 1200);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Reverse failed");
        } finally {
            setReverseLoading(false);
        }
    };

    // ── Reverse Applications ──────────────────────────────────────
    const handleReverseApplications = async (e) => {
        e.preventDefault();
        if (!selectedRows.length) return;
        setReverseAppLoading(true);
        try {
            const res = await axios.post(
                API + `creditmemo/${creditMemoId}/reverse-applications/`,
                { invoice_ids: selectedRows },
                { headers: authHeader() }
            );
            if (res.data.success === 1) {
                toast.success(res.data.message, { autoClose: 1000 });
                setSelectedRows([]);
                setTimeout(() => loadCM(), 1200);
            } else {
                toast.error(res.data.message || "Reversal failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Reversal failed");
        } finally {
            setReverseAppLoading(false);
        }
    };

    // ── Upload ────────────────────────────────────────────────────
    const handleUpload = async () => {
        if (!selectedFile) return;
        try {
            const res = await CreditMemoService.uploadAttachment(creditMemoId, selectedFile);
            setAttachments((prev) => [...prev, res.data]);
            setSelectedFile(null);
            toast.success("Attachment uploaded", { autoClose: 1000 });
        } catch {
            toast.error("Upload failed");
        }
    };

    // ── Document pagination ────────────────────────────────────────
    const totalPages = Math.ceil(attachments.length / recordsPerPage);
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentDocuments = attachments.slice(indexOfFirstRecord, indexOfLastRecord);

    if (loading || !cm) {
        return (
            <ContentLoader viewBox="0 0 400 160">
                <rect x="0" y="13" rx="4" ry="4" width="400" height="9" />
                <rect x="0" y="29" rx="4" ry="4" width="100" height="8" />
                <rect x="0" y="50" rx="4" ry="4" width="400" height="10" />
            </ContentLoader>
        );
    }

    const unapplied = parseFloat(((cm.amount || 0) - (cm.applied_amount || 0)).toFixed(2));
    const canEdit = cm.status === "new";
    const canReverse = (cm.applied_amount || 0) > 0;
    const isApplied = (cm.applied_amount || 0) > 0;
    

    return (
        <>
            <ToastContainer />
            <div className="container-fluid">

                {/* ── Header ── */}
                <div className="header pt-3 mb-3">
                    <h5>{t("Credit Memo")}</h5>
                    <h6>
                        <Link to="/creditmemo" className="tc">{t("Credit Memos")}</Link>
                        {" "} / <span>{t("View Credit Memo")}</span>
                    </h6>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="d-flex">

                            {/* ── LEFT col-md-7 — Label/Value pairs ── */}
                            <div className="col-md-7">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row">



                                            {/* Row: Sub Company | Line of Business */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Sub Company")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.subcompany_name || "None"}</span>
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Line of Business")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{cm.line_of_business_name || "None"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Company | CM Number */}
                                            <div className="row">

                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Credit Memo Number")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{cm.credit_memo_number || "None"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Customer | Credit Amount */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Customer")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.customer_name || "None"}</span>
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Credit Amount")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{Helper.Amount(cm.amount)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Date | Applied Amount */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Date")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.credit_memo_date ? Helper.DateNormal(cm.credit_memo_date) : "None"}</span>
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Applied Amount")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{Helper.Amount(cm.applied_amount || 0)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Reason | Unapplied Amount */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Reason")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.reason || "None"}</span>
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Unapplied Amount")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{Helper.Amount(unapplied)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Reference | Status */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Reference")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.reference || "None"}</span>
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Status")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{cm.status || "None"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Description | Approval Status */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Description")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.description || "None"}</span>
                                                    </div>
                                                </div>
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Approval Status")}</label>
                                                    <div className="col-xl-6 d-flex">
                                                        <span className="party-detail-font">{cm.approval_status || "None"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Row: Reverse Reason */}
                                            <div className="row">
                                                <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                    <label className="sub-title tc">{t("Reverse Reason")}</label>
                                                    <div className="col-xl-6 d-flex" style={{ justifyContent: 'space-between' }}>
                                                        <span className="party-detail-font">{cm.reverse_reason || "None"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                        {/* ── Action Buttons ── */}

                                        {/* <button
                                            type="button"
                                            className="btn-apply border-0 btn btn-primary mt-3 btn-gradient-primary btn-sm"
                                            style={{ marginLeft: '10px' }}
                                            onClick={() => navigate("/creditmemo", { state: creditMemoId })}
                                        >
                                            {t("Back")}
                                        </button> */}


                                        {(!isApprovalRequired ||
                                            (isApprovalRequired &&
                                                (approvalStatus === "approved" || approvalStatus === "rejected"))) &&
                                            (
                                                <button
                                                    type="button"
                                                    className="btn-apply border-0 btn btn-primary mt-3 btn-gradient-primary btn-sm"
                                                    style={{ marginLeft: "10px" }}
                                                    onClick={() =>
                                                        document
                                                            .getElementById("applied-invoices-section")
                                                            ?.scrollIntoView({ behavior: "smooth" })
                                                    }
                                                >
                                                    {t("Application Reversal")}
                                                </button>
                                            )}

                                        {(!isApprovalRequired ||
                                            (isApprovalRequired &&
                                                (approvalStatus === "approved" || approvalStatus === "rejected"))) &&
                                            (
                                                <button
                                                    type="button"
                                                    disabled={reverseLoading}
                                                    className="btn-apply border-0 btn btn-primary mt-3 btn-gradient-primary btn-sm"
                                                    style={{ marginLeft: "10px" }}
                                                    onClick={handleReverseConfirm}
                                                >
                                                    {reverseLoading
                                                        ? t("Reversing...")
                                                        : t("Reverse Credit Memo")}
                                                </button>
                                            )}

                                    </div>
                                </div>
                            </div>

                            {/* ── RIGHT col-md-5 — Documents ── */}
                            <div className="col-md-5">
                                <div className="card">
                                    <div className="card-body">

                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="mb-0">{t("Documents")} ({attachments.length})</h5>
                                            {canEdit && (
                                                <>
                                                    <input
                                                        type="file"
                                                        id="cm-file-upload"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => setSelectedFile(e.target.files[0])}
                                                    />
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => {
                                                            if (selectedFile) {
                                                                handleUpload();
                                                            } else {
                                                                document.getElementById('cm-file-upload').click();
                                                            }
                                                        }}
                                                    >
                                                        {selectedFile ? "SAVE" : "UPLOAD"}
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        <div className="documents-list">
                                            {attachments.length === 0 ? (
                                                <p className="text-muted text-center small">No documents</p>
                                            ) : (
                                                <>
                                                    {currentDocuments.map((doc) => (
                                                        <div className="document-item" key={doc.id}>
                                                            <div className="document-icon">📄</div>
                                                            <div className="document-info">
                                                                <div className="document-name">
                                                                    {doc.file?.split("/").pop() || "File"}
                                                                </div>
                                                                <div className="document-metadata">
                                                                    {doc.uploaded_at ? Helper.DateNormal(doc.uploaded_at) : ""}
                                                                </div>
                                                            </div>
                                                            <div className="document-actions">
                                                                <button
                                                                    className="btn-continue border-0 btn btn-primary btn-gradient-primary btn"
                                                                    onClick={() => window.open(doc.file_url || doc.file, "_blank")}
                                                                >
                                                                    Download
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            )}

                                            {/* Pagination — same as receipt */}
                                            <div className="pagination-controls d-flex justify-content-between mt-3">
                                                <button
                                                    className="pag-btn"
                                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    Previous
                                                </button>
                                                <span>Page {currentPage} of {totalPages || 0}</span>
                                                <button
                                                    className="pag-btn"
                                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                                {
                                    isApprovalRequired && cm.status === "New" && (
                                        <div className="card mt-3">

                                            {
                                                approvalStatus?.toLowerCase() === "approved" ||
                                                    approvalStatus?.toLowerCase() === "rejected" ? (

                                                    <ApprovalDetails
                                                        approvalHistory={approvalHistory}
                                                        approvalRequestId={approvalRequestId}
                                                    />

                                                ) : (
                                                    approvalRequest && (
                                                        <ApprovalAction
                                                            request={approvalRequest}
                                                            onSuccess={() => window.location.reload()}
                                                        />
                                                    )
                                                )
                                            }

                                        </div>
                                    )
                                }
                            </div>

                        </div>
                    </div>
                </div>

                {/* ── Applied Invoices Table (full width, below) ── */}
                {isApplied && (
                    <div className="pt-4 mt-2" id="applied-invoices-section">
                        <h5 className="mb-3">{t("Applied Invoices")}</h5>
                        <MaterialTable
                            style={{
                                backgroundColor: "var(--bs-table-bg)",
                                borderBottomWidth: "1px",
                                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                            }}
                            columns={[
                                {
                                    title: t("Invoice Number"),
                                    field: "invoice_number",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => (
                                        <div className="hyperlink" onClick={() => navigate("/billing/viewinvoices", { state: row.invoice_id })}>
                                            {row.invoice_number}
                                        </div>
                                    ),
                                },
                                {
                                    title: t("Transaction Date"),
                                    field: "creation_date",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => Helper.DateNormal(row.creation_date),
                                },
                                {
                                    title: t("Due Date"),
                                    field: "due_date",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => row.due_date ? Helper.DateNormal(row.due_date) : "-",
                                },
                                {
                                    title: t("Invoice Amount"),
                                    field: "total_amount",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "right" },
                                    cellStyle: { textAlign: "right", fontSize: 12 },
                                    render: (row) => <div style={{ textAlign: "right" }}>{Helper.Amount(row.total_amount)}</div>,
                                },
                                {
                                    title: t("Applied Amount"),
                                    field: "apply_amount",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "right" },
                                    cellStyle: { textAlign: "right", fontSize: 12 },
                                    render: (row) => <div style={{ textAlign: "right" }}>{Helper.Amount(row.apply_amount)}</div>,
                                },
                                {
                                    title: t("Contract No"),
                                    field: "contract_id",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => row.contract_id?.contract_number || "-",
                                },
                                {
                                    title: t("Customer Name"),
                                    field: "customer_name",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => row.customer_name || cm.customer_name || "-",
                                },
                            ]}
                            data={appliedInvoices}
                            onSelectionChange={(rows) => {
                                const uniqueIds = [...new Set(rows.map(r => r.invoice_id))];
                                setSelectedRows(uniqueIds);
                            }}
                            options={{
                                headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 },
                                rowStyle: (_, i) => i % 2 ? { backgroundColor: "#fff" } : {},
                                sorting: true,
                                search: false,
                                toolbar: false,
                                paging: true,
                                pageSize: 10,
                                emptyRowsWhenPaging: false,
                                pageSizeOptions: [10, 20, 50],
                                actionsColumnIndex: -1,
                                selection: true,
                                showTextRowsSelected: false,
                            }}
                        />
                        {selectedRows.length > 0 && (
                            <div className="mt-2">
                                <button
                                    className="btn btn-primary btn-sm"
                                    disabled={reverseAppLoading}
                                    onClick={handleReverseApplications}
                                >
                                    {reverseAppLoading ? t("Reversing...") : t("Reverse Application")}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Open Invoices Table (full width, below) ── */}
                {unapplied > 0 && dueInvoices.length > 0 && (
                    <div className="pt-3 mt-2">
                        <h5 className="mb-3">{t("Open Invoices")}</h5>
                        <MaterialTable
                            style={{
                                backgroundColor: "var(--bs-table-bg)",
                                borderBottomWidth: "1px",
                                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                            }}
                            columns={[
                                {
                                    title: t("Invoice Number"),
                                    field: "invoice_number",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => (
                                        <div className="hyperlink" onClick={() => navigate("/billing/viewinvoices", { state: row.id })}>
                                            {row.invoice_number}
                                        </div>
                                    ),
                                },
                                {
                                    title: t("Transaction Date"),
                                    field: "creation_date",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => Helper.DateNormal(row.creation_date),
                                },
                                {
                                    title: t("Due Date"),
                                    field: "due_date",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => row.due_date ? Helper.DateNormal(row.due_date) : "-",
                                },
                                {
                                    title: t("Invoice Amount"),
                                    field: "total_amount",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "right" },
                                    cellStyle: { textAlign: "right", fontSize: 12 },
                                    render: (row) => <div className="d-none d-xl-block text-right" style={{ textAlign: "right", fontSize: 12 }}>{Helper.Amount(row.total_amount)}</div>,
                                },
                                {
                                    title: t("Remaining Amount"),
                                    field: "balance_amount",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "right" },
                                    cellStyle: { textAlign: "right", fontSize: 12 },
                                    render: (row) => <div className="d-none d-xl-block text-right" style={{ textAlign: "right", fontSize: 12 }}>{Helper.Amount(row.balance_amount)}</div>,
                                },
                                {
                                    title: t("Day Past Due"),
                                    field: "due_date",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => Helper.DueDays(row.due_date),
                                },
                                {
                                    title: t("Contract No"),
                                    field: "contract_id",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => row.contract_id?.contract_number || "-",
                                },
                                {
                                    title: t("Customer Name"),
                                    field: "customer_name",
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (row) => row.customer_name || cm.customer_name || "-",
                                },
                                {
                                    title: t("Apply Amount"),
                                    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                                    render: (row) => (
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            style={{ width: "100px" }}
                                            min="0"
                                            step="0.01"
                                            max={Math.min(row.balance_amount || 0, unapplied)}
                                            value={applyAmounts[row.id] || ""}
                                            onChange={(e) => setApplyAmounts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                            placeholder="0.00"
                                        />
                                    ),
                                },
                                {
                                    title: "",
                                    render: (row) => (
                                        <button
                                            className="btn-apply border-0 btn btn-primary btn-gradient-primary btn-sm"
                                            onClick={() => handleApplyToInvoice(row)}
                                            disabled={applyLoading === row.id}
                                        >
                                            {applyLoading === row.id ? "..." : t("APPLY")}
                                        </button>
                                    ),
                                },
                            ]}
                            data={dueInvoices}
                            options={{
                                headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 },
                                rowStyle: (_, i) => i % 2 ? { backgroundColor: "#fff" } : {},
                                sorting: true,
                                search: false,
                                toolbar: false,
                                paging: true,
                                pageSize: 10,
                                emptyRowsWhenPaging: false,
                                pageSizeOptions: [10, 20, 25, 50],
                                paginationType: "stepped",
                                showFirstLastPageButtons: false,
                                actionsColumnIndex: -1,
                            }}
                        />

                    </div>
                )}

            </div>
        </>
    );
}
